/**
 * Математика калькулятора робота.  Повторяет правила роботов:
 *   • крипта (robots/bybit/robot.py, режим safe): позиция набирается по
 *     «безопасной кривой» — чем дальше цена от средней, тем больше, до
 *     лимита при отходе fullDrawdown %;
 *   • сложный процент (shared/utils.py::calculate_ratio): лот и лимит
 *     умножаются на ratio, который растёт с депозитом выше 3,75 × минимума.
 * Худший случай для риска: цена идёт против без откатов, робот доливается
 * при каждой возможности.
 */

export const PLATFORM_SHARE = 0.5 // комиссия RealColibri — 50% чистой прибыли
export const RECOMMENDED_DEPOSIT_RATIO = 3.75
/**
 * Тейк больше — сделок чуть меньше: рынок ходит волнами (ES — по 4–5
 * пунктов), и где берётся 1 пункт, берутся и 2, и 3.  Оценка владельца:
 * −10–15% сделок, когда тейк больше на величину исторического (ES с тейком
 * 1 п.: −12,5% за каждый лишний пункт).  Уточним по году реальной торговли.
 */
export const TRADES_DROP_PER_TAKE = 0.125

/** Во сколько раз меньше (больше) сделок с тейком take, если история — с historyTake. */
export const tradesFactorFor = (take: number, historyTake: number) =>
    historyTake > 0
        ? (1 - TRADES_DROP_PER_TAKE) ** ((take - historyTake) / historyTake)
        : 1

export interface CryptoCalcInput {
    price: number // текущая цена
    takeProfit: number // тейк в цене
    lot: number // начальный лот клиента, монет
    maxMultiplier: number // лимит позиции = лот × множитель
    fullDrawdown: number // %, отход от средней, при котором лимит заполнен
    takerFee: number // %, вход по рынку
    makerFee: number // %, выход лимиткой
    leverage: number
    maintenanceMarginRate: number // %, если ступеней нет
    riskTiers?: { limit: number; mmr: number }[] // ступени маржи по объёму, %
    direction: Direction // лонг — цена против = падает, шорт — растёт
    deposit: number
    minBalance: number
    tradesPerDay: number
    daysPerMonth: number
    movePct: number // сценарий: цена против позиции, %
    /** Чистая прибыль в день на 1 монету начального лота по реальным сделкам. */
    historyNetPerLotDay?: number
    /** Монет в день на 1 монету лота и тейк, с которыми набрана история. */
    historyVolumePerLotDay?: number
    historyTake?: number | null
}

export type Direction = 'long' | 'short'

export interface PathPoint {
    x: number // ход цены против позиции от входа, в цене
    lots: number
    avg: number // средняя цена входа: на сколько она дальше первой цены против позиции
}

export interface CryptoCalcResult {
    ratio: number // множитель сложного процента
    effectiveLot: number // лот, которым робот торгует сейчас
    maxLots: number
    grossPerTrade: number
    feePerTrade: number
    netPerTrade: number
    netPerDay: number
    monthBeforePlatform: number
    platformFee: number
    monthClient: number
    fullAt: number | null // ход цены, при котором лимит заполнен
    marginCallAt: number | null // ход цены до маржин-колла
    scenarioMove: number
    scenarioLots: number
    scenarioLoss: number
    recoverMove: number // нужный откат от худшей точки для выхода по тейку
    path: PathPoint[]
    step: number
}

/** Ставка поддерживающей маржи для объёма позиции — по ступеням биржи, %. */
export const mmrFor = (notional: number, input: CryptoCalcInput) => {
    const tiers = input.riskTiers
    if (!tiers?.length) return input.maintenanceMarginRate
    const tier = tiers.find(t => notional <= t.limit) ?? tiers[tiers.length - 1]
    return tier.mmr
}

/** shared/utils.py::calculate_ratio — ступени по 0,1 выше 3,75 × минимума. */
export const compoundRatio = (balance: number, minBalance: number) => {
    if (!minBalance || balance <= minBalance * RECOMMENDED_DEPOSIT_RATIO)
        return 1
    return (
        Math.trunc((balance / (minBalance * RECOMMENDED_DEPOSIT_RATIO)) * 10) /
        10
    )
}

/** Цена, когда она ушла на x против позиции. */
export const priceAtMove = (input: CryptoCalcInput, x: number) =>
    input.direction === 'short' ? input.price + x : input.price - x

const buildPath = (lot: number, maxLots: number, input: CryptoCalcInput) => {
    const { price, fullDrawdown } = input
    const step = price * 0.0002
    // Лонг: цена может упасть почти до нуля.  Шорт: рост не ограничен — смотрим до ×6.
    const xMax = input.direction === 'short' ? price * 5 : price * 0.99
    let lots = lot
    let avg = 0
    let fullAt: number | null = null
    const path: PathPoint[] = [{ x: 0, lots, avg }]
    for (let x = step; x <= xMax + 1e-12; x += step) {
        if (lots < maxLots - 1e-9) {
            // Отход от средней цены входа, % (как в роботе: от средней, не от первой).
            const ddPct = ((x - avg) / priceAtMove(input, avg)) * 100
            const target =
                lot +
                (maxLots - lot) * Math.min(Math.max(ddPct / fullDrawdown, 0), 1)
            if (target > lots + 1e-9) {
                const add = Math.min(target, maxLots) - lots
                avg = (lots * avg + add * x) / (lots + add)
                lots += add
            }
            if (lots >= maxLots - 1e-9 && fullAt === null) fullAt = x
        }
        path.push({ x, lots, avg })
    }
    return { path, step, fullAt }
}

export const pointAt = (path: PathPoint[], step: number, x: number) =>
    path[Math.max(0, Math.min(Math.round(x / step), path.length - 1))]

export const computeCrypto = (input: CryptoCalcInput): CryptoCalcResult => {
    const ratio = compoundRatio(input.deposit, input.minBalance)
    const lot = input.lot * ratio
    const maxLots = lot * input.maxMultiplier
    const { path, step, fullAt } = buildPath(lot, maxLots, input)

    const grossPerTrade = lot * input.takeProfit
    const feePerTrade =
        (lot * input.price * (input.takerFee + input.makerFee)) / 100
    const netPerTrade = grossPerTrade - feePerTrade
    // Есть история сделок — берём реальный доход (он включает выходы после
    // доливок, которые дают большую часть прибыли); нет — модель «вход по тейку».
    // Тейк клиента не тот, с которым набрана история: валовая прибыль
    // истории (чистыми + уже вычтенная комиссия) — пропорционально тейку,
    // сделок — по tradesFactorFor, комиссия — с нового объёма.
    const historyDay = () => {
        const net = input.historyNetPerLotDay!
        const take = input.historyTake
        const volume = input.historyVolumePerLotDay
        if (!take || !volume || Math.abs(take - input.takeProfit) < 1e-12)
            return net * lot
        const tf = tradesFactorFor(input.takeProfit, take)
        const feePerCoin =
            (input.price * (input.takerFee + input.makerFee)) / 100
        const gross =
            (net + volume * feePerCoin) * (input.takeProfit / take) * tf
        return (gross - volume * tf * feePerCoin) * lot
    }
    const netPerDay =
        input.historyNetPerLotDay !== undefined
            ? historyDay()
            : netPerTrade * input.tradesPerDay
    const monthBeforePlatform = netPerDay * input.daysPerMonth
    const platformFee =
        monthBeforePlatform > 0 ? monthBeforePlatform * PLATFORM_SHARE : 0

    const loss = (p: PathPoint) => p.lots * (p.x - p.avg)
    const maintenance = (p: PathPoint) => {
        const notional = p.lots * priceAtMove(input, p.x)
        return (mmrFor(notional, input) / 100) * notional
    }
    let marginCallAt: number | null = null
    for (const p of path) {
        if (input.deposit - loss(p) - maintenance(p) <= 0) {
            marginCallAt = p.x
            break
        }
    }

    const scenarioMove = (input.price * input.movePct) / 100
    const s = pointAt(path, step, scenarioMove)
    return {
        ratio,
        effectiveLot: lot,
        maxLots,
        grossPerTrade,
        feePerTrade,
        netPerTrade,
        netPerDay,
        monthBeforePlatform,
        platformFee,
        monthClient: monthBeforePlatform - platformFee,
        fullAt,
        marginCallAt,
        scenarioMove,
        scenarioLots: s.lots,
        scenarioLoss: loss(s),
        recoverMove: s.x - s.avg + input.takeProfit,
        path,
        step,
    }
}

/** Рост депозита по месяцам: прибыль остаётся на счёте, лот растёт по ratio. */
export const compoundProjection = (
    input: CryptoCalcInput,
    months: number[]
) => {
    const out: { month: number; deposit: number }[] = []
    let deposit = input.deposit
    for (let m = 1; m <= Math.max(...months); m++) {
        const r = computeCrypto({ ...input, deposit })
        deposit += Math.max(r.monthClient, 0)
        if (months.includes(m)) out.push({ month: m, deposit })
    }
    return out
}

export type RiskLevel = 'good' | 'warn' | 'bad'

export const riskLevel = (r: CryptoCalcResult, deposit: number): RiskLevel => {
    if (r.netPerTrade <= 0) return 'bad'
    if (r.marginCallAt !== null && r.scenarioMove >= r.marginCallAt)
        return 'bad'
    const dd = r.scenarioLoss / deposit
    if (
        dd >= 0.35 ||
        (r.marginCallAt !== null && r.marginCallAt < r.scenarioMove * 1.25)
    )
        return 'bad'
    if (dd >= 0.15) return 'warn'
    return 'good'
}

/**
 * Безопасный депозит: при каком депозите худшее движение цены из истории
 * инструмента (вниз — для лонга, вверх — для шорта) забирает не больше
 * SAFE_DRAWDOWN депозита и не доводит до маржин-колла.  Считается для лота
 * без сложного процента: выше рекомендованного депозита робот увеличивает
 * лот пропорционально депозиту, и риск в процентах остаётся тем же.
 */
export const SAFE_DRAWDOWN = 0.3
export const SAFE_WINDOW_DAYS = 30

export interface SafeSide {
    movePct: number
    lots: number
    loss: number
    maintenance: number
    deposit: number // нужный депозит для этой стороны
}

const sideAt = (
    input: CryptoCalcInput,
    direction: Direction,
    movePct: number
): SafeSide => {
    const i = { ...input, direction }
    const maxLots = input.lot * input.maxMultiplier
    const { path, step } = buildPath(input.lot, maxLots, i)
    const p = pointAt(path, step, (input.price * movePct) / 100)
    const loss = p.lots * (p.x - p.avg)
    const notional = p.lots * priceAtMove(i, p.x)
    const maintenance = (mmrFor(notional, i) / 100) * notional
    return {
        movePct,
        lots: p.lots,
        loss,
        maintenance,
        deposit: Math.max(loss / SAFE_DRAWDOWN, loss + maintenance),
    }
}

export const safeDeposit = (
    input: CryptoCalcInput,
    downPct: number,
    upPct: number
) => {
    const long = sideAt(input, 'long', downPct)
    const short = sideAt(input, 'short', upPct)
    return { long, short, deposit: Math.max(long.deposit, short.deposit) }
}
